import { Injectable } from '@angular/core';
import { Entity } from 'cesium';
import * as Cesium from 'cesium';
import { FocalPoint } from '../models/fixed-perimeter.models';
import { ReportMapEntity } from './report-map-entity.service';
import { TextEntityAttribute } from './text-entity-attribute.service';
import { DefaultEditCommand } from '../models/command';
import { CameraService } from './camera.service';
import { CoreService } from './core.service';
import { WindowService } from './window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataContextService } from './data-context.service';
import { TimelineService } from './timeline.service';

export class FocalPointMapEntity extends ReportMapEntity {
  private eventId?: string;
  private geometry: FocalPoint;
  override description: string;
  private isRejected = false;

  constructor(
    focalpoint: FocalPoint,
    createdAt: Date,
    cameraService: CameraService,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService,
    userId?: string,
    eventName?: string,
    eventId?: string
  ) {
    const lat = focalpoint.coordinates[1];
    const lon = focalpoint.coordinates[0];
    const description = `Focal Point for event: ${eventName || 'Unknown'}`;
    const validCreatedAt = createdAt instanceof Date && !isNaN(createdAt.getTime())
    ? createdAt
    : new Date();
    super(
      '',
      truncateString(description, 25),
      'ArcAngel//Focal Points',
      'assets/img/arcangel/focalpoint.png',
      lat,
      lon,
      0,
      validCreatedAt, '',
      cameraService,
      coreService,
      windowService,
      dataContext,
      modalService,
      timelineService
      
    );
  
    this.setShowInLayersUI(false);

    this.eventId = eventId;
    this.geometry = focalpoint;
    this.description = description;

    this.addAttribute(new TextEntityAttribute('Description', this.getId(), 'description'));
    this.buildEntity();
    for (let i = this.commands.length - 1; i >= 0; i--) {
      if (this.commands[i] instanceof DefaultEditCommand) {
        this.removeCommand(this.commands[i]);
      }
    }
  }

  getEventId(): string | undefined {
    return this.eventId;
  }

  getDescription(): string {
    return this.description;
  }

  getGeometry(): FocalPoint {
    return this.geometry;
  }

  getData(): FocalPoint {
    return this.geometry;
  }

  getLastUpdate(): Date {
    return this.getLastUpdate();
  }

  override getShowIn2d(): boolean {
    return false;
  }

  override getShowIn3d(): boolean {
    if (this.isRejected) {
      return false;
    }
    return true;
  }

  public override buildEntity(): void {
    this.cesiumEntity = {
      id: this.id,
      name: this.name,
      position: Cesium.Cartesian3.fromDegrees(this.longitude, this.latitude, this.altitude),
      billboard: {
        image: this.iconUrl,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        scale: 0.75,
        // Use NONE for faster rendering of focal points
        heightReference: Cesium.HeightReference.NONE 
      },
      description: this.description || (this.source ? `Source: ${this.source}` : undefined)
    };
    if (this.timestamp) {
      const start = Cesium.JulianDate.fromDate(this.timestamp);
      const end = Cesium.JulianDate.addDays(start, 365, new Cesium.JulianDate());
      this.cesiumEntity.availability = new Cesium.TimeIntervalCollection([
        new Cesium.TimeInterval({ start, stop: end })
      ]);
    }
    this.cesiumEntity.arcLiveMapEntity = this;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FocalPointMapEntityService {
  createFocalPointEntity(
    focalpoint: FocalPoint,
    createdAt: Date = new Date(),
    cameraService: CameraService,
    coreService: CoreService,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService,
    userId?: string,
    eventName?: string,
    eventId?: string,
  ): FocalPointMapEntity {
    return new FocalPointMapEntity(focalpoint, createdAt, cameraService, coreService, windowService, dataContext, modalService, timelineService);
  }
}

function truncateString(str: string, num: number): string {
  return str.length <= num ? str : str.slice(0, num) + '...';
}